import { IonContent, IonItem, IonLabel, IonModal } from "@ionic/react";
import React, { useState, useEffect, useRef } from "react";
import { Helmet } from "react-helmet";

const emojis = ["🐘", "🐭", "🚀", "✈️", "🏠", "🛖", "🚌", "🚗", "🌳", "🌿", "🏰", "⛺", "🍎", "🍕", "🐶", "🐱", "⚽", "🏀", "🎈", "🎸"];

const LeftRightGame: React.FC = () => {
    const [leftEmoji, setLeftEmoji] = useState<string>("");
    const [rightEmoji, setRightEmoji] = useState<string>("");
    const [questionSide, setQuestionSide] = useState<string>(""); // "left" or "right"
    const [correctEmoji, setCorrectEmoji] = useState<string>("");
    const [message, setMessage] = useState("");
    const [showImage, setShowImage] = useState(false);
    const modal = useRef<HTMLIonModalElement>(null);

    useEffect(() => {
        generateQuestion();
        document.title = "Left and Right – LKG Pre-Math Worksheet | Mama’s Guidance"; // Set page title
    }, []);

    const generateQuestion = () => {
        const randomEmojis = [...emojis];

        // Pick two different random emojis
        const emoji1 = randomEmojis.splice(Math.floor(Math.random() * randomEmojis.length), 1)[0];
        const emoji2 = randomEmojis.splice(Math.floor(Math.random() * randomEmojis.length), 1)[0];

        // Randomly decide which side the question should ask for
        const isAskingLeft = Math.random() < 0.5;

        setLeftEmoji(emoji1);
        setRightEmoji(emoji2);
        setQuestionSide(isAskingLeft ? "left" : "right");
        setCorrectEmoji(isAskingLeft ? emoji1 : emoji2);
        setMessage("");
    };

    const checkAnswer = (selectedEmoji: string) => {
        if (selectedEmoji === correctEmoji) {
            setMessage("✔️ Correct!");
            setTimeout(generateQuestion, 2000);
        } else {
            setMessage("❌ Try Again!");
        }
    };

    return (
        <>
            <Helmet>
                <meta name="description" content='Teach LKG students the concepts of left and right with engaging activities on Mama’s Guidance—designed to build directional awareness and early spatial understanding through interactive and fun visual exercises.' />
            </Helmet>
            {
                showImage === false ?
                    <>
                        <div className="contentPart additionWord heavyLight matchImage match-container game-container">
                            <div className="showInfo" style={{ width: '80%' }} onClick={() => modal.current?.present()}><i className="fas fa-info-circle"></i></div>
                            <IonModal
                                ref={modal}
                                trigger="open-modal"
                                initialBreakpoint={1}
                                breakpoints={[0, 0.5, 0.75, 1]}
                                handleBehavior="cycle"
                                backdropDismiss={false}  // ✅ Prevents click-to-close
                                className="full-page-modal"
                            >
                                <IonContent className="ion-padding">
                                    <div style={{ textAlign: 'right', fontSize: '25px' }} onClick={() => modal.current?.dismiss()}><i className="fas fa-3a fa-times"></i></div>
                                    <h1>Left and Right</h1>

                                    <p>
                                        One of the earliest skills in spatial awareness is identifying directions — and this <strong>Left and Right</strong> worksheet is designed to help LKG children learn it through fun and simple visuals. With colorful examples and hands-on practice, kids will begin to distinguish between left and right in everyday life.
                                    </p>

                                    <h2>🔄 What Do Left and Right Mean?</h2>
                                    <p>
                                        <strong>Left</strong> and <strong>Right</strong> are basic directions used to describe the position of objects or movement. These concepts help children <strong>develop navigation, coordination, and observation skills</strong> early on.
                                    </p>

                                    <h2>🧩 What’s in the Worksheet?</h2>
                                    <ul>
                                        <li>Visual examples like shoes, hands, or animals facing left or right</li>
                                        <li>Circle the object on the left/right</li>
                                        <li>Draw arrows in the correct direction</li>
                                        <li>Match left/right labels with appropriate images</li>
                                        <li>Interactive activities to reinforce learning</li>
                                    </ul>

                                    <h2>🎯 Learning Objectives</h2>
                                    <ul>
                                        <li>Understand and differentiate between <strong>left and right</strong></li>
                                        <li>Improve directional vocabulary</li>
                                        <li>Enhance spatial reasoning and body awareness</li>
                                        <li>Support pre-writing and motor coordination skills</li>
                                    </ul>

                                    <h2>👩‍🏫 How to Use the Worksheet</h2>
                                    <ul>
                                        <li>Start by showing your child their left and right hand.</li>
                                        <li>Use pictures in the worksheet to ask questions like <strong>“Which toy is on the left?”</strong></li>
                                        <li>Guide them to circle or color objects on the correct side.</li>
                                        <li>Play a game of <strong>“Raise your left/right hand”</strong> or <strong>“Turn left/right”</strong> for real-world learning.</li>
                                    </ul>


                                    <h2>🔗 Related Topics</h2>
                                    <div>
                                        <IonItem routerLink="/lkg/mathematics/pre-math-concepts/long-short"
                                            routerDirection="none"
                                            lines="none"
                                            detail={true}>
                                            <IonLabel>Long - Short</IonLabel>
                                        </IonItem>
                                        <IonItem routerLink="/lkg/mathematics/pre-math-concepts/big-small"
                                            routerDirection="none"
                                            lines="none"
                                            detail={true}>
                                            <IonLabel>Big - Small</IonLabel>
                                        </IonItem>
                                        <IonItem routerLink="/lkg/mathematics/pre-math-concepts/full-empty"
                                            routerDirection="none"
                                            lines="none"
                                            detail={true}>
                                            <IonLabel>Full - Empty</IonLabel>
                                        </IonItem>
                                    </div>

                                    <h2>🧠 Why This Concept Is Important</h2>
                                    <p>
                                        Mastering <strong>left and right</strong> helps children understand directions, follow instructions, and improve coordination. It also supports reading (left to right flow) and handwriting orientation.
                                    </p>

                                    <div className="btn hero-btn" onClick={() => modal.current?.dismiss()}>Close</div>
                                </IonContent>
                            </IonModal>
                            <article className="visually-hidden">
                                <h1>Left and Right</h1>

                                <p>
                                    One of the earliest skills in spatial awareness is identifying directions — and this <strong>Left and Right</strong> worksheet is designed to help LKG children learn it through fun and simple visuals. With colorful examples and hands-on practice, kids will begin to distinguish between left and right in everyday life.
                                </p>

                                <h2>🔄 What Do Left and Right Mean?</h2>
                                <p>
                                    <strong>Left</strong> and <strong>Right</strong> are basic directions used to describe the position of objects or movement. These concepts help children <strong>develop navigation, coordination, and observation skills</strong> early on.
                                </p>

                                <h2>🧩 What’s in the Worksheet?</h2>
                                <ul>
                                    <li>Visual examples like shoes, hands, or animals facing left or right</li>
                                    <li>Circle the object on the left/right</li>
                                    <li>Draw arrows in the correct direction</li>
                                    <li>Match left/right labels with appropriate images</li>
                                    <li>Interactive activities to reinforce learning</li>
                                </ul>

                                <h2>🎯 Learning Objectives</h2>
                                <ul>
                                    <li>Understand and differentiate between <strong>left and right</strong></li>
                                    <li>Improve directional vocabulary</li>
                                    <li>Enhance spatial reasoning and body awareness</li>
                                    <li>Support pre-writing and motor coordination skills</li>
                                </ul>

                                <h2>👩‍🏫 How to Use the Worksheet</h2>
                                <ul>
                                    <li>Start by showing your child their left and right hand.</li>
                                    <li>Use pictures in the worksheet to ask questions like <strong>“Which toy is on the left?”</strong></li>
                                    <li>Guide them to circle or color objects on the correct side.</li>
                                    <li>Play a game of <strong>“Raise your left/right hand”</strong> or <strong>“Turn left/right”</strong> for real-world learning.</li>
                                </ul>


                                <h2>🔗 Related Topics</h2>
                                <div>
                                    <IonItem routerLink="/lkg/mathematics/pre-math-concepts/long-short"
                                        routerDirection="none"
                                        lines="none"
                                        detail={true}>
                                        <IonLabel>Long - Short</IonLabel>
                                    </IonItem>
                                    <IonItem routerLink="/lkg/mathematics/pre-math-concepts/big-small"
                                        routerDirection="none"
                                        lines="none"
                                        detail={true}>
                                        <IonLabel>Big - Small</IonLabel>
                                    </IonItem>
                                    <IonItem routerLink="/lkg/mathematics/pre-math-concepts/full-empty"
                                        routerDirection="none"
                                        lines="none"
                                        detail={true}>
                                        <IonLabel>Full - Empty</IonLabel>
                                    </IonItem>
                                </div>

                                <h2>🧠 Why This Concept Is Important</h2>
                                <p>
                                    Mastering <strong>left and right</strong> helps children understand directions, follow instructions, and improve coordination. It also supports reading (left to right flow) and handwriting orientation.
                                </p>
                            </article>
                            <h2>Which one is on the {questionSide}?</h2>
                            <div style={{ display: "flex", justifyContent: "center", gap: "100px", fontSize: "50px" }}>
                                <div onClick={() => checkAnswer(leftEmoji)} style={{ cursor: "pointer" }}>
                                    {leftEmoji}
                                </div>
                                <div onClick={() => checkAnswer(rightEmoji)} style={{ cursor: "pointer" }}>
                                    {rightEmoji}
                                </div>
                            </div>
                            <p style={{ fontSize: "20px", marginTop: "20px" }}>{message}</p>
                        </div>
                    </>
                    :
                    <>
                        <div className="contentPart showMiddle">
                            <img style={{ height: '150px' }} className='img-fluid' src={process.env.REACT_APP_WELDONE_LINK} />
                        </div>
                    </>
            }
        </>
    );
};

export default LeftRightGame;
